import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import styles from './styles';
import classnames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { getWeekSheets } from 'store/schedule/schedulePageOperation';
import { add, format } from 'date-fns';
import { globalBEDateFormat } from 'common/dateFormatConfig';
import WeeklyEventCard from './WeeklyEventCard/WeeklyEventCard';
import useV6WeekEvents from '../hooks/useV6WeekEvents';


const WeekViewCalendar = ({
  classes,
  noClSheets,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedDate = useSelector((store) => store.schedule.selectedDate);
  const filters = useSelector((state) => state.schedule.filters);
  const showAllLocations = useSelector((state) => state.schedule.showAllLocations);
  const { events } = useV6WeekEvents();

  const renderDays = useMemo(() => {
    return new Array(7)
      .fill({ date: selectedDate })
      .map((v, i) => {
        const date = add(selectedDate, { days: i });
        return {
          date,
          count: events[format(date, globalBEDateFormat)]?.length,
          events: events[format(date, globalBEDateFormat)],
        };
      });
  }, [selectedDate, events])

  const fetchSheets = React.useCallback(
    (requestFilters) => {
      dispatch(
        getWeekSheets({
          filters: requestFilters,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    fetchSheets({ ...filters, allLocations: showAllLocations });
  }, [selectedDate, filters, showAllLocations, fetchSheets]);

  const isDateToday = (date) => (
    format(date, globalBEDateFormat) === format(new Date(), globalBEDateFormat)
  )

  const renderHeaderCountText = (count = 0) => `${count} Worklog${count === 1 ? '' : 's'}`


  const handleClick = (sheet) => (event) => {
    event.preventDefault();
    if (!sheet) return;
    const baseDate = selectedDate ? new Date(selectedDate) : new Date();
    const dateStr = format(baseDate, 'yyyy-MM-dd');
    navigate(`/schedule/${dateStr}?sheetId=${sheet._id}`);
  };

  return (
    <table className={classnames(classes.weekCalendarWrapper)}>
      <thead className={classnames(classes.header)}>
        <tr>
          {renderDays.map((el, i) => (
            <th
              className={classnames(
                classes.headerCell,
                isDateToday(el.date) ? classes.todayHeaderCell : null
              )}
              key={`${i}_header`}
              scope="col"
            >
              <div className={classes.headerCellContent}>
                <div className={classes.headerDateGroup}>
                  <div className={classes.headerDate}>{format(el.date, 'dd')}</div>
                  <div className={classes.headerWeekDay}>{format(el.date, 'ccc')}</div>
                </div>
                <div className={classes.headerCountGroup}>
                  {isDateToday(el.date) ? (<div className={classes.todayBadge}>TODAY</div>) : null}
                  <div className={classes.headerCount}>{renderHeaderCountText(el.count)}</div>
                </div>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {renderDays.map((el, i) => (
            <td
              key={`${i}_cell`}
              className={classes.bodyCell}
            >
              <div className={classes.bodyCellScroller}>
                {el.events?.map((event) => (
                  <WeeklyEventCard
                    handleClick={handleClick(event.sheet)}
                    event={event}
                    noClSheets={noClSheets}
                  />
                ))}
              </div>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
};

WeekViewCalendar.propTypes = {
  noClSheets: PropTypes.arrayOf(PropTypes.string),
}

export default withStyles(styles)(WeekViewCalendar);
